/**
 * Login Page
 *
 * User login with email/password and MFA support.
 */

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';

export function LoginPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError, mfaMethod } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMFA, setShowMFA] = useState(false);

  // Get redirect destination
  const from = (location.state as { from?: Location })?.from?.pathname || '/formspace';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    const result = await login({
      email,
      password,
      mfaCode: showMFA ? mfaCode : undefined,
      mfaMethod: showMFA ? (mfaMethod as 'totp' | 'email' | 'backup') : undefined,
    });

    if (result.success) {
      navigate(from, { replace: true });
    } else if (result.requiresMFA) {
      setShowMFA(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">{t('auth.appName')}</h1>
          <h2 className="mt-6 text-2xl font-semibold text-gray-900">
            {showMFA ? t('auth.twoFactorAuth') : t('auth.signInTitle')}
          </h2>
          {!showMFA && (
            <p className="mt-2 text-sm text-gray-600">
              {t('auth.signInSubtitle')}{' '}
              <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                {t('auth.createNewAccount')}
              </Link>
            </p>
          )}
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {!showMFA ? (
            /* Email and Password */
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('auth.emailAddress')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('auth.emailPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('auth.password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('auth.passwordPlaceholder')}
                />
              </div>
            </div>
          ) : (
            /* MFA Code */
            <div>
              <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700">
                {mfaMethod === 'totp'
                  ? t('auth.enterAuthenticatorCode')
                  : mfaMethod === 'email'
                    ? t('auth.enterEmailCode')
                    : t('auth.enterBackupCode')}
              </label>
              <input
                id="mfaCode"
                name="mfaCode"
                type="text"
                autoComplete="one-time-code"
                required
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
              />
              <p className="mt-2 text-sm text-gray-500">
                <button
                  type="button"
                  onClick={() => {
                    setShowMFA(false);
                    setMfaCode('');
                    clearError();
                  }}
                  className="text-blue-600 hover:text-blue-500"
                >
                  {t('auth.goBackToLogin')}
                </button>
              </p>
            </div>
          )}

          {/* Forgot password link */}
          {!showMFA && (
            <div className="flex items-center ltr:justify-end rtl:justify-start">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('auth.signingIn')}
              </span>
            ) : showMFA ? (
              t('auth.verify')
            ) : (
              t('auth.signIn')
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
